import { useEffect, useRef } from 'react';
import { Button, Stack, Step } from '@sixui/ui';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import stylex from '@stylexjs/stylex';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import type { IPackagePreparerStepProps } from './PackagePreparerStep';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { FileAsset } from '~/components/molecules/FileAsset';
import { AssetUploader } from '~/components/organisms/fields/AssetUploader';
import {
  appResourcesQueryKeyFactory,
  useAppResourcesQuery,
} from '~/lib/api/modules/AppResources/hooks/useAppResourcesQuery';
import { commonStyles } from '~/styles/commonStyles';

export type IPackagePreparerAppIconStepProps = IPackagePreparerStepProps;

const styles = stylex.create({
  assetUploader: {
    width: 144,
    height: 144,
    borderRadius: 25.263,
  },
  thumb: {
    width: 57,
    height: 57,
    borderRadius: 20,
    objectFit: 'cover',
    borderWidth: 1,
    borderColor: colorSchemeTokens.outlineVariant,
    borderStyle: 'solid',
  },
});

export const PackagePreparerAppIconStep: React.FC<
  IPackagePreparerAppIconStepProps
> = (props) => {
  const { appId, onSuccess, platform, ...other } = props;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const settingsQuery = useAppResourcesQuery({ appId });
  const assets = settingsQuery.data;

  const isInitRef = useRef(false);
  const completed =
    !!assets && !!assets.platforms?.[platform]?.appIcon?.downloadUrl;
  useEffect(() => {
    if (!assets || isInitRef.current) {
      return;
    }

    isInitRef.current = true;
    if (completed) {
      onSuccess?.({
        source: 'init',
      });
    }
  }, [assets, completed, onSuccess]);

  return (
    <Step
      {...other}
      label={t('PackagePreparerAppIconStep.name')}
      alwaysExpanded={!other.disabled}
    >
      {settingsQuery.isError ? (
        <ErrorMessage error={settingsQuery.error} />
      ) : settingsQuery.isLoading ? (
        t('common.loading')
      ) : assets ? (
        ({ active }) =>
          active ? (
            <Stack gap={4} align="start">
              <Trans
                parent="p"
                i18nKey="PackagePreparerAppIconStep.description"
              />
              <AssetUploader
                appId={appId}
                type={platform}
                format="icon"
                accept={{ 'image/png': ['.png'] }}
                downloadUrl={assets.platforms?.[platform]?.appIcon?.downloadUrl}
                onChange={(hasAsset) => {
                  if (hasAsset) {
                    void queryClient.invalidateQueries({
                      queryKey: appResourcesQueryKeyFactory({ appId }),
                    });
                  }
                }}
                sx={styles.assetUploader}
                renderFileAsset={(elementProps) => (
                  <FileAsset
                    {...elementProps}
                    sx={styles.assetUploader}
                    hideMetadata
                  />
                )}
              />
              <Button
                onClick={
                  onSuccess ? () => onSuccess({ source: 'user' }) : undefined
                }
                disabled={!assets.platforms?.[platform]?.appIcon?.downloadUrl}
              >
                {t('common.next')}
              </Button>
            </Stack>
          ) : assets.platforms?.[platform]?.appIcon?.downloadUrl ? (
            <img
              {...stylex.props(
                styles.thumb,
                commonStyles.transparentCheckerboardBackground,
              )}
              src={assets.platforms[platform].appIcon.downloadUrl}
              alt=""
            />
          ) : null
      ) : null}
    </Step>
  );
};

PackagePreparerAppIconStep.displayName = 'Step';
